<template>
  <div class="py-6 space-y-4">
    <!-- === HEADER === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h1 class="text-2xl font-bold text-gray-800">
        {{ levelData ? 'Modifier le niveau' : 'Nouveau niveau' }}
      </h1>
      <p class="text-sm text-gray-500 mt-1">
        {{ levelData ? `Niveau ${levelData.level}` : 'Créer un nouveau niveau de fidélité' }}
      </p>
    </div>

    <!-- === FORMULAIRE === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <form @submit.prevent="saveLevel" class="space-y-4">
        <!-- Niveau -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Niveau
          </label>
          <input
            v-model.number="form.level"
            type="number"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent"
            placeholder="1"
          />
        </div>

        <!-- Nom -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Nom
          </label>
          <input
            v-model="form.name"
            type="text"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent"
            placeholder="Nouveau client"
          />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent"
            placeholder="Découvre la plateforme et passe ta première commande"
          />
        </div>

        <!-- XP disponibles -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            XP requis pour ce niveau
          </label>
          <input
            v-model.number="form.availableXp"
            type="number"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent"
            placeholder="10"
          />
        </div>

        <!-- Récompenses du niveau -->
        <div class="space-y-3">
          <label class="block text-sm font-medium text-gray-700">
            Récompenses du niveau
          </label>
          
          <!-- Liste des récompenses -->
          <div v-if="form.rewards && form.rewards.length > 0" class="space-y-2">
            <div 
              v-for="(reward, index) in form.rewards" 
              :key="index"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
            >
              <div class="flex items-center gap-3">
                <span v-if="reward.type === 'xp'" class="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">✨</span>
                <span v-else-if="reward.type === 'coupon'" class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">🎟️</span>
                <span v-else-if="reward.type === 'wallet'" class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">💰</span>
                <span v-else class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">🎁</span>
                
                <div>
                  <div class="text-sm font-medium text-gray-800">
                    {{ getRewardLabel(reward) }}
                  </div>
                  <div class="text-xs text-gray-500 capitalize">
                    {{ reward.type || 'Personnalisé' }}
                  </div>
                </div>
              </div>
              
              <button 
                type="button"
                @click="removeReward(index)"
                class="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Supprimer"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>
          </div>
          
          <!-- Ajout d'une récompense -->
          <div class="flex flex-col sm:flex-row gap-2 mt-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100 border-dashed">
            <select 
              v-model="newReward.type"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
            >
              <option value="xp">Points XP</option>
              <option value="coupon">Coupon (Code)</option>
              <option value="wallet">Montant Portefeuille</option>
            </select>
            
            <input 
              v-model="newReward.value"
              :type="newReward.type === 'xp' || newReward.type === 'wallet' ? 'number' : 'text'"
              :placeholder="getRewardPlaceholder()"
              class="flex-[2] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
              @keyup.enter="addReward"
            />
            
            <button 
              type="button"
              @click="addReward"
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
              :disabled="!newReward.value"
            >
              Ajouter
            </button>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-4">
          <button
            type="button"
            @click="$emit('hide')"
            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            {{ levelData ? 'Mettre à jour' : 'Créer' }}
          </button>
        </div>
      </form>
    </div>

    <!-- === DÉFIS === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6" v-if="false">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">Défis du niveau</h3>
          <p class="text-xs text-gray-500 mt-1">Configurez les défis permettant de gagner de l'XP sur ce niveau</p>
        </div>
        <button
          type="button"
          @click="addMilestone"
          class="px-3 py-1.5 text-sm font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
        >
          + Ajouter un défi
        </button>
      </div>

      <div v-if="form.milestones && form.milestones.length > 0" class="space-y-2">
        <div
          v-for="(milestone, index) in form.milestones"
          :key="milestone.id || index"
          class="flex items-center justify-between p-3 bg-gray-50 rounded-lg group border border-transparent hover:border-gray-200 transition-colors"
        >
          <div class="flex items-center gap-3">
            <span class="text-lg">{{ milestone.icon || '🏆' }}</span>
            <div>
              <div class="text-sm font-medium text-gray-800 flex items-center gap-2">
                {{ milestone.name }}
                <span v-if="milestone.targetValue" class="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                  CIBLE: {{ milestone.targetValue }}
                </span>
              </div>
              <div class="text-xs text-gray-500">
                {{ milestone.description }}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div class="text-sm font-medium text-gray-600">
              +{{ milestone.rewardXp }} XP
            </div>
            <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                @click.prevent="editMilestone(milestone)"
                class="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Modifier"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              <button
                @click.prevent="deleteMilestone(index)"
                class="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Supprimer"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="py-6 text-center border-2 border-dashed border-gray-100 rounded-xl">
        <p class="text-gray-500 text-sm">Aucun défi configuré pour ce niveau</p>
      </div>
    </div>

    <!-- === MODAL DÉFI === -->
    <div v-if="showMilestoneEditor" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" @click.self="showMilestoneEditor = false">
      <div class="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar shadow-2xl">
        <MilestoneEditor
          :milestoneData="editingMilestone"
          @saved="onMilestoneSaved"
          @cancel="showMilestoneEditor = false"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import MilestoneEditor from '../gamification/admin/MilestoneEditor.vue'

const props = defineProps({
  levelData: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['hide', 'saved'])

const form = reactive({
  level: 1,
  name: '',
  description: '',
  availableXp: 10,
  rewards: [],
  milestones: [],
})

watch(() => props.levelData, (newData) => {
  if (newData) {
    form.level = newData.level
    form.name = newData.name
    form.description = newData.description
    form.availableXp = newData.requiredXp
    
    // Mapping des récompenses pour supporter l'ancien et le nouveau format
    form.rewards = (newData.rewards || []).map(r => {
      if (typeof r === 'string') {
        return { type: 'custom', value: r, label: r }
      }
      return r
    })
    
    form.milestones = newData.milestones || []
  } else {
    resetForm()
  }
}, { immediate: true })

function resetForm() {
  form.level = 1
  form.name = ''
  form.description = ''
  form.availableXp = 10
  form.rewards = []
  form.milestones = []
}

function saveLevel() {
  const payload = {
    ...form,
    id: props.levelData?.id || `level_${Date.now()}`,
    operation: props.levelData ? 'update' : 'add',
    requiredXp: form.availableXp,
    rewards: form.rewards,
  }

  emit('saved', payload)
}

// Reward management logic
const newReward = reactive({
  type: 'xp',
  value: ''
})

const getRewardPlaceholder = () => {
  if (newReward.type === 'xp') return 'Ex: 100'
  if (newReward.type === 'coupon') return 'Code coupon (ex: WELCOME)'
  if (newReward.type === 'wallet') return 'Montant en FCFA (ex: 500)'
  return 'Valeur...'
}

const getRewardLabel = (reward) => {
  if (reward.label) return reward.label
  if (reward.type === 'xp') return `+${reward.value} XP`
  if (reward.type === 'coupon') return `Coupon : ${reward.value}`
  if (reward.type === 'wallet') return `Crédit Portefeuille : ${reward.value} FCFA`
  return reward.value
}

const addReward = () => {
  if (!newReward.value) return
  
  form.rewards.push({
    type: newReward.type,
    value: newReward.type === 'coupon' ? newReward.value : Number(newReward.value),
    label: getRewardLabel({ type: newReward.type, value: newReward.value })
  })
  
  newReward.value = ''
}

const removeReward = (index) => {
  form.rewards.splice(index, 1)
}

// Milestone state & handlers
const showMilestoneEditor = ref(false)
const editingMilestone = ref(null)

const addMilestone = () => {
  editingMilestone.value = null
  showMilestoneEditor.value = true
}

const editMilestone = (milestone) => {
  editingMilestone.value = { ...milestone }
  showMilestoneEditor.value = true
}

const deleteMilestone = (index) => {
  if (confirm("Supprimer ce défi ?")) {
    form.milestones.splice(index, 1)
  }
}

const onMilestoneSaved = (milestone) => {
  if (editingMilestone.value && editingMilestone.value.id) {
    const idx = form.milestones.findIndex(m => m.id === milestone.id)
    if (idx !== -1) {
      form.milestones[idx] = milestone
    } else {
      form.milestones.push(milestone)
    }
  } else {
    form.milestones.push(milestone)
  }
  showMilestoneEditor.value = false
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
